<template>
  <div>
    <Molded class="bank-list-header" :paddingTop="16" :paddingRight="20" :paddingBottom="16" :paddingLeft="20">
      <div class="bank-list-toolbar">
        <Button _key="btnBankCreate" title="Banco" classIcon="fas fa-plus-circle" type="primary" size="small"
          :clicked="create" />
      </div>

      <div class="bank-list-filter">
        <div class="bank-list-filter-search">
          <Search />
        </div>
      </div>

      <section class="bank-list-topbar">
        <TotalRecordsBadge :total="totalRecords" />
      </section>
    </Molded>

    <Loading v-if="loading" type="line" :center="false" class="bank-list-loading" />

    <div v-if="!loading && banks.length === 0" class="bank-list-empty">
      <i class="fa-solid fa-building-columns"></i>
      <span>Nenhum banco encontrado</span>
    </div>

    <div class="bank-list-grid">
      <BankCard v-for="bank in banks" :key="bank.id" :bank="bank" :selected="isSelected(bank)" @edit="edit"
        @select="toggleSelect" @delete="openDeleteModal" />
    </div>

    <LoadingMoreButton :loading="loadingMore" :remaining="totalRecords - banks.length" @click="loadMore" />

    <BottomActionsBar :selected="selectedIds" @close="cleanSelected">
      <ActionsSelected :selected="selectedIds" :actions="actions" />
    </BottomActionsBar>

    <Modal title="Banco" :width="500" :height="250" v-show="showModal('bankCreateUpdate')">
      <BankCreateUpdate />
    </Modal>

    <Modal title="Excluir Banco" :width="450" v-if="showModal('bankDelete')">
      <div class="text-center">
        <p class="bank-list-delete-text">Tem certeza que deseja excluir este banco?</p>
        <Confirmation :isModal="false" title="Deseja excluir?" type="danger" :confirmed="remove" />
      </div>
    </Modal>
  </div>
</template>

<script>
import Button from "@/ui/forms/Button";
import Modal from "@/ui/forms/Modal";

import BottomActionsBar from "@/ui/layout/BottomActionsBar";
import Molded from "@/ui/layout/Molded";
import TotalRecordsBadge from "@/ui/layout/TotalRecordsBadge.vue";

import ActionsSelected from "@/ui/shared/actions/ActionsSelected.vue";
import Confirmation from "@/ui/shared/Confirmation.vue";
import Loading from "@/ui/shared/Loading.vue";
import LoadingMoreButton from "@/ui/shared/LoadingMoreButton.vue";
import Search from "@/ui/shared/Search.vue";

import BankCard from "./BankCard.vue";
import BankCreateUpdate from "./BankCreateUpdate.vue";

import BankService from "../../services/bank.service";

import { mapGetters, mapMutations, mapState } from "vuex";

export default {
  name: "BankList",
  components: {
    Button,
    Modal,
    BottomActionsBar,
    Molded,
    TotalRecordsBadge,
    ActionsSelected,
    Confirmation,
    Loading,
    LoadingMoreButton,
    Search,
    BankCard,
    BankCreateUpdate,
  },
  data() {
    return {
      banks: [],
      totalRecords: 0,
      selectedIds: [],
      bankToDelete: {},
      currentPage: 1,
      initialPageSize: 12,
      loadMoreSize: 4,
      loading: true,
      loadingMore: false,
      actions: [
        {
          type: "delete",
          title: "Remover",
          description: "Remover",
          url: BankService.urlDelete,
          method: "post",
        },
      ],
    };
  },
  mounted() {
    this.list();
  },
  beforeDestroy() {
    this.updateSearch({ content: "", filter: { content: "Contém", id: "contains" } });
  },
  computed: {
    ...mapState("generic", ["search", "executedSearch", "clearedSearch"]),
    ...mapGetters("generic", ["showModal", "event"]),
    hasMore() {
      return this.banks.length < this.totalRecords;
    },
  },
  methods: {
    ...mapMutations("generic", ["openModal", "hideModal", "addEvent", "removeLoading", "updateSearch"]),
    async list() {
      this.loading = true;
      this.currentPage = 1;
      try {
        const response = await BankService.getAll({
          search: this.search.content,
          filter: this.search.filter.id || "contains",
          currentPage: this.currentPage,
          totalPerPage: this.initialPageSize,
        });
        this.banks = response.content.items || [];
        this.totalRecords = response.content.totalRecords || 0;
      } finally {
        this.loading = false;
      }
    },
    async loadMore() {
      if (this.loadingMore || !this.hasMore) return;
      this.loadingMore = true;
      try {
        this.currentPage = Math.floor(this.banks.length / this.loadMoreSize) + 1;
        const response = await BankService.getAll({
          search: this.search.content,
          filter: this.search.filter.id || "contains",
          currentPage: this.currentPage,
          totalPerPage: this.loadMoreSize,
        });
        this.banks = [...this.banks, ...(response.content.items || [])];
        this.totalRecords = response.content.totalRecords || 0;
      } finally {
        this.loadingMore = false;
      }
    },
    create() {
      this.openModal("bankCreateUpdate");
      this.addEvent({ name: "bankCreate" });
      this.removeLoading(["btnBankCreate"]);
    },
    edit(bank) {
      this.openModal("bankCreateUpdate");
      this.addEvent({ name: "bankUpdate", data: bank });
    },
    isSelected(bank) {
      return this.selectedIds.includes(bank.id);
    },
    toggleSelect(bank) {
      if (this.isSelected(bank)) {
        this.selectedIds = this.selectedIds.filter((id) => id != bank.id);
        return;
      }
      this.selectedIds = [...this.selectedIds, bank.id];
    },
    cleanSelected() {
      this.selectedIds = [];
    },
    openDeleteModal(bank) {
      this.bankToDelete = bank;
      this.openModal("bankDelete");
    },
    async remove() {
      try {
        await BankService.delete([this.bankToDelete.id]);
        this.hideModal();
        this.selectedIds = this.selectedIds.filter((id) => id != this.bankToDelete.id);
        await this.list();
      } finally {
        this.removeLoading(["confirm", "notConfirm"]);
      }
    },
  },
  watch: {
    executedSearch: function () {
      this.currentPage = 1;
      this.list();
      this.removeLoading(["btnSearch"]);
    },
    clearedSearch: function () {
      this.currentPage = 1;
      this.list();
      this.removeLoading(["btnClean"]);
    },
    event: {
      handler(event) {
        if (event.name == "bankSaved") this.list();
        if (event.name == "deselectAll") this.cleanSelected();
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
.bank-list-header {
  margin-bottom: 16px;
}

.bank-list-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
}

.bank-list-filter {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin: 16px 0;
}

.bank-list-filter> :last-child {
  flex-shrink: 0;
}

.bank-list-filter-search {
  flex: 1 1 320px;
  min-width: 0;
}

.bank-list-filter-search>>>.margin {
  padding-top: 0;
}

.bank-list-filter-search>>>.div-button {
  padding-top: 0;
}

.bank-list-loading {
  margin-bottom: 20px;
}

.bank-list-grid {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 16px;
}

.bank-list-topbar {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 10px;
}

.bank-list-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 48px 0;
  color: var(--text-muted);
  font-size: 14px;
}

.bank-list-empty i {
  font-size: 40px;
  color: var(--color-brand-light);
}

.bank-list-delete-text {
  font-size: 14px;
  color: var(--text-primary);
  margin-bottom: 14px;
}

.bank-list-delete-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 12px 16px;
  border: 1px solid var(--border-subtle);
  border-radius: 12px;
  background: var(--bg-elevated);
  margin-bottom: 20px;
}

.bank-list-delete-name {
  font-size: 15px;
  font-weight: 500;
  color: var(--text-primary);
}

.bank-list-delete-description {
  font-size: 12px;
  color: var(--text-muted);
  font-style: italic;
}
</style>
